import { useMemo, useSyncExternalStore } from 'react';

// Minimal hash router (no dependency). Hash routing works unchanged inside
// Capacitor web views and on static PWA hosting.
//   #/home  #/discover  #/create[/post|propose|request]  #/calendar  #/me
//   #/activity/:id  #/profile/:id  #/collab/:id  #/share/:kitId

export type Tab = 'home' | 'discover' | 'create' | 'calendar' | 'me';
export type Detail = 'activity' | 'profile' | 'collab' | 'share';
export type Route = { name: Tab; sub?: string } | { name: Detail; id: string };

const TABS: readonly Tab[] = ['home', 'discover', 'create', 'calendar', 'me'];
const DETAILS: readonly Detail[] = ['activity', 'profile', 'collab', 'share'];
const hasWindow = typeof window !== 'undefined';

export function parse(path: string): Route {
  const [n, id] = path.replace(/^#?\/?/, '').split('/').filter(Boolean).map(decodeURIComponent);
  if (DETAILS.includes(n as Detail)) return id ? { name: n as Detail, id } : { name: 'home' };
  if (TABS.includes(n as Tab)) return { name: n as Tab, sub: id };
  return { name: 'home' };
}

export function toPath(r: Route): string {
  if ('id' in r) return `/${r.name}/${encodeURIComponent(r.id)}`;
  return r.sub ? `/${r.name}/${r.sub}` : `/${r.name}`;
}

const current = (): string => (hasWindow ? window.location.hash.replace(/^#/, '') : '') || '/home';
const listeners = new Set<() => void>();
let stack: string[] = [current()];
let lastTab: Tab = tabOf(current()) ?? 'home';

function tabOf(path: string): Tab | null {
  const r = parse(path);
  return 'id' in r ? null : r.name;
}

function emit(): void {
  listeners.forEach((l) => l());
}

if (hasWindow) {
  window.addEventListener('hashchange', () => {
    const cur = current();
    if (stack.length > 1 && stack[stack.length - 2] === cur) stack.pop();
    else if (stack[stack.length - 1] !== cur) stack.push(cur);
    lastTab = tabOf(cur) ?? lastTab;
    emit();
  });
}

export function navigate(r: Route, opts: { replace?: boolean } = {}): void {
  const p = toPath(r);
  const t = tabOf(p);
  if (t && !opts.replace) stack = stack.slice(0, 0); // tab roots start a fresh stack
  if (opts.replace || p === current()) {
    window.history.replaceState(null, '', `#${p}`);
    stack[Math.max(0, stack.length - 1)] = p;
    if (t) lastTab = t;
    emit();
    return;
  }
  window.location.hash = p;
}

export function back(): void {
  if (stack.length > 1) window.history.back();
  else navigate({ name: lastTab }, { replace: true });
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useRoute(): { route: Route; path: string; tab: Tab } {
  const path = useSyncExternalStore(subscribe, current, () => '/home');
  return useMemo(() => ({ route: parse(path), path, tab: tabOf(path) ?? lastTab }), [path]);
}
